/**
 * 월드 이동 확인 팝업 — GridUnlockConfirmPopup 패턴(ModalLayer + NineSlicePanel).
 * 이동 시 리셋되는 범위를 설명하고 Yes/No 버튼을 제공한다.
 */
import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import { CONTENT_PANEL_STYLE, FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { popupCloseButtonX } from "./directionalUi.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";
import {
  resolveWorldMoveConfirmLayout,
  WORLD_MOVE_CONFIRM_PANEL_H,
  WORLD_MOVE_CONFIRM_PANEL_W,
} from "./worldMoveConfirmLayout.js";

export interface WorldMoveConfirmPopupOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly worldName: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export class WorldMoveConfirmPopup extends ModalLayer {
  constructor(scene: Phaser.Scene, opts: WorldMoveConfirmPopupOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => opts.onCancel(),
    });

    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    const L = resolveWorldMoveConfirmLayout(opts.screenWidth, opts.screenHeight);
    this.setBackdropCloseExclusion(L.panel.x, L.panel.y, L.panel.width, L.panel.height);

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: WORLD_MOVE_CONFIRM_PANEL_W,
      height: WORLD_MOVE_CONFIRM_PANEL_H,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    const title = scene.add
      .text(L.title.x + L.title.width / 2, L.title.y + L.title.height / 2, t("world.moveConfirm.title", { world: opts.worldName }), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textBright,
        wordWrap: { width: L.title.width },
        align: "center",
      })
      .setOrigin(0.5);
    const closeBtn = scene.add
      .text(popupCloseButtonX(cx, WORLD_MOVE_CONFIRM_PANEL_W, 18, getTextDirection()), L.close.y + L.close.height / 2, "\u2715", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => opts.onCancel());
    this.contentContainer.add([title, closeBtn]);

    // 리셋 안내 본문
    const bodyText = t("world.moveConfirm.body");
    const body = scene.add
      .text(L.body.x + L.body.width / 2, L.body.y, bodyText, {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textSecondary,
        wordWrap: { width: L.body.width },
        align: "center",
        lineSpacing: 4,
      })
      .setOrigin(0.5, 0);
    this.contentContainer.add(body);

    // Yes 버튼
    const yesBtn = new NineSliceButton(scene, L.yesButton.x + L.yesButton.width / 2, L.yesButton.y + L.yesButton.height / 2, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("world.moveConfirm.yes"),
      widthOverride: L.yesButton.width,
      heightOverride: L.yesButton.height,
    });
    yesBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onConfirm());
    // No 버튼
    const noBtn = new NineSliceButton(scene, L.noButton.x + L.noButton.width / 2, L.noButton.y + L.noButton.height / 2, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("world.moveConfirm.no"),
      widthOverride: L.noButton.width,
      heightOverride: L.noButton.height,
    });
    noBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onCancel());
    this.contentContainer.add([yesBtn, noBtn]);
  }
}
